import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';
import { DateInput } from '../../../../shared/components/DateInput.js';

interface RateCardRow {
  readonly rateId: string;
  readonly rateCode: string;
  readonly vendorName: string | null;
  readonly brandingTypeName: string | null;
  readonly brandingItemName: string | null;
  readonly geoNodeName: string | null;
  readonly unitOfMeasure: string;
  readonly rateValue: string;
  readonly currencyCode: string;
  readonly effectiveFromDate: string;
  readonly effectiveToDate: string;
  readonly approvalStatus: string;
  readonly sourceWorkOrderNumber: string | null;
  readonly expired: boolean;
}

interface RateCardResponse {
  readonly rows: RateCardRow[];
  readonly options: {
    readonly vendors: Array<{ vendorId: string; vendorName: string }>;
    readonly types: Array<{ brandingTypeId: string; brandingTypeName: string }>;
    readonly items: Array<{ brandingItemId: string; brandingItemName: string }>;
  };
}

/** Indian financial year: 1 Apr to 31 Mar. */
function financialYear(offset: number): { fromDate: string; toDate: string; label: string } {
  const now = new Date();
  const y = (now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1) + offset;
  return {
    fromDate: `${y}-04-01`,
    toDate: `${y + 1}-03-31`,
    label: `${y}-${String((y + 1) % 100).padStart(2, '0')}`,
  };
}

const ALL_TIME = { fromDate: '1900-01-01', toDate: '9999-12-31' };

/** L17.01 — Rate card (`/admin/rate-master/cards`).
 *
 *  Rates arrive from two directions: entered by hand, and published when a
 *  branding work order is approved. An approval closes the previous overlapping
 *  rate rather than deleting it, so the default view is the live one and the
 *  closed rows stay reachable under Historical. */
export function M17RateCardListPage(): ReactNode {
  const { t } = useTranslation();
  // Dates follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { day } = useBusinessDay();
  const { decimal } = useBusinessNumber();
  const { token } = useAuth();
  const [data, setData] = useState<RateCardResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [view, setView] = useState('LATEST');
  const [vendorId, setVendorId] = useState('ALL');
  const [brandingTypeId, setBrandingTypeId] = useState('ALL');
  const [brandingItemId, setBrandingItemId] = useState('ALL');
  const [source, setSource] = useState('ALL');
  const [period, setPeriod] = useState('CURRENT_FY');
  const [customFrom, setCustomFrom] = useState(financialYear(0).fromDate);
  const [customTo, setCustomTo] = useState(financialYear(0).toDate);

  const range = useMemo(() => {
    if (period === 'CURRENT_FY') return financialYear(0);
    if (period === 'PREVIOUS_FY') return financialYear(-1);
    if (period === 'ALL') return ALL_TIME;
    return { fromDate: customFrom, toDate: customTo };
  }, [period, customFrom, customTo]);

  const load = useCallback(async () => {
    setData(null);
    const qs = new URLSearchParams({
      view, vendorId, brandingTypeId, brandingItemId, source,
      fromDate: range.fromDate, toDate: range.toDate,
    });
    try {
      const r = await apiRequest<RateCardResponse>(`/v1/m17/rates/rate-card?${qs.toString()}`, { token });
      setData(r);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [token, view, vendorId, brandingTypeId, brandingItemId, source, range]);

  useEffect(() => { void load(); }, [load]);

  if (error) return <p role="alert">{error}</p>;

  return (
    <main aria-label="rate-card-list">
      <h1>{t('m17.rateCardList.title')}</h1>
      <Link to="/admin/rate-master/cards/new" aria-label="new-rate-card">{t('m17.rateCardList.newRateCard')}</Link>

      <section aria-label="rate-card-filters">
        <label>
          View
          <select aria-label="filter-view" value={view} onChange={(e) => setView(e.target.value)}>
            <option value="LATEST">Latest (in force)</option>
            <option value="HISTORICAL">Historical (closed)</option>
            <option value="ALL">All</option>
          </select>
        </label>

        <label>
          Vendor
          <select aria-label="filter-vendor" value={vendorId} onChange={(e) => setVendorId(e.target.value)}>
            <option value="ALL">All vendors</option>
            {(data?.options.vendors ?? []).map((v) => (
              <option key={v.vendorId} value={v.vendorId}>{v.vendorName}</option>
            ))}
          </select>
        </label>

        <label>
          Period
          <select aria-label="filter-period" value={period} onChange={(e) => setPeriod(e.target.value)}>
            <option value="CURRENT_FY">Current FY ({financialYear(0).label})</option>
            <option value="PREVIOUS_FY">Previous FY ({financialYear(-1).label})</option>
            <option value="ALL">All dates</option>
            <option value="CUSTOM">Custom range</option>
          </select>
        </label>

        {period === 'CUSTOM' && (
          <>
            <label>
              From
              <DateInput type="date" aria-label="filter-from" value={customFrom} onValueChange={(nextValue) => setCustomFrom(nextValue)} />
            </label>
            <label>
              To
              <DateInput type="date" aria-label="filter-to" value={customTo} onValueChange={(nextValue) => setCustomTo(nextValue)} />
            </label>
          </>
        )}

        <label>
          Branding type
          <select aria-label="filter-branding-type" value={brandingTypeId} onChange={(e) => setBrandingTypeId(e.target.value)}>
            <option value="ALL">All types</option>
            {(data?.options.types ?? []).map((x) => (
              <option key={x.brandingTypeId} value={x.brandingTypeId}>{x.brandingTypeName}</option>
            ))}
          </select>
        </label>

        <label>
          Sub-type
          <select aria-label="filter-branding-item" value={brandingItemId} onChange={(e) => setBrandingItemId(e.target.value)}>
            <option value="ALL">All sub-types</option>
            {(data?.options.items ?? []).map((x) => (
              <option key={x.brandingItemId} value={x.brandingItemId}>{x.brandingItemName}</option>
            ))}
          </select>
        </label>

        <label>
          Source
          <select aria-label="filter-source" value={source} onChange={(e) => setSource(e.target.value)}>
            <option value="ALL">All sources</option>
            <option value="WORK_ORDER">From approved work order</option>
            <option value="MANUAL">Entered manually</option>
          </select>
        </label>
      </section>

      {!data ? (
        <p>{t('m17.rateCardList.loading')}</p>
      ) : (
        <>
          <table aria-label="rate-card-table">
            <thead>
              <tr>
                <th>{t('m17.rateCardList.colCode')}</th>
                <th>{t('m17.rateCardList.colVendor')}</th>
                <th>{t('m17.rateCardList.colBranding')}</th>
                <th>Sub-type</th>
                <th>Geography</th>
                <th>Rate</th>
                <th>UOM</th>
                <th>{t('m17.rateCardList.colFrom')}</th>
                <th>{t('m17.rateCardList.colTo')}</th>
                <th>{t('m17.rateCardList.colStatus')}</th>
                <th>Source</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.rateId}>
                  <td>
                    <Link to={`/admin/rate-master/cards/${r.rateId}`} aria-label={`view-${r.rateCode}`}>{r.rateCode}</Link>
                  </td>
                  <td>{r.vendorName ?? t('m17.rateCardList.generic')}</td>
                  <td>{r.brandingTypeName ?? '—'}</td>
                  <td>{r.brandingItemName ?? '—'}</td>
                  <td>{r.geoNodeName ?? 'All geographies'}</td>
                  <td>{r.currencyCode} {decimal(r.rateValue)}</td>
                  <td>{r.unitOfMeasure}</td>
                  <td>{day(r.effectiveFromDate)}</td>
                  <td>{day(r.effectiveToDate)}</td>
                  <td>{r.expired ? `${r.approvalStatus} (closed)` : r.approvalStatus}</td>
                  <td>{r.sourceWorkOrderNumber ?? 'Manual'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {data.rows.length === 0 && <p>No rates match these filters.</p>}
        </>
      )}
    </main>
  );
}
